// Shared agent create/edit form. The schema fields that take complex shapes
// (tools, allowed domains/origins, fallback) are surfaced as JSON / line-list
// textareas — full structured editors land in 5.1b. Validation is local
// before the request is fired so the user sees field errors instantly; the
// API still re-validates server-side.
//
// Required-field UX (the GOV.UK / GitHub pattern):
//   • Required fields render a visible asterisk after the label, with
//     aria-required on the input. Non-required fields keep "(optional)" in
//     their visible label where space allows.
//   • On submit failure: the form prepends an <ErrorSummary> alert listing
//     every problem with click-to-focus links, scrolls the page so the
//     summary is visible, and focuses its heading (screen-reader announce).
//   • Each invalid input gets aria-invalid=true so the destructive border
//     state already baked into our Input/Textarea/Select primitives fires.
//   • Editing a field with an error clears that field's error live, so the
//     summary shrinks as the user fixes things — they see progress.

import { useEffect, useMemo, useState, type FormEvent, type ReactElement, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MODELS_BY_PROVIDER,
  MODEL_PRICE_RANK,
  PROVIDERS as SHARED_PROVIDERS,
  cheapestAllowedModel,
  cheapestProviderFor,
  type Agent,
  type AgentFallback,
  type AgentModelPolicy,
  type CreateAgentInput,
  type Provider,
  type ToolDefinition,
} from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { friendlyError } from '../../lib/error-message.js';
import { agentsApi, AGENTS_QUERY_KEY, agentQueryKey } from '../../lib/api/agents.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { Textarea } from '../../components/ui/textarea.js';
import { toast } from '../../components/ui/toaster.js';
import { ErrorSummary, type ErrorSummaryItem } from '../../components/ui/error-summary.js';

// Display order for the Provider <Select>. Shared exports `['gemini',
// 'openai', 'anthropic']`; we keep that order so the cheapest provider
// appears first in the dropdown UX (gemini owns the cheapest model today).
const PROVIDERS = SHARED_PROVIDERS;

interface FormState {
  name: string;
  systemPrompt: string;
  provider: Provider;
  model: string;
  temperature: string;
  maxOutputTokens: string;
  maxCostUsd: string;
  toolsJson: string;
  allowedDomainsText: string;
  allowedOriginsText: string;
  fallbackEnabled: boolean;
  fallbackProvider: Provider;
  fallbackModel: string;
}

// Hard-coded fallback when allowedModels hasn't loaded yet (or returned
// empty for a mis-seeded plan_limits row). Gemini Flash Lite is the
// cheapest model overall and is on every shipping plan today, so it's a
// safe pre-load default — emptyForm() below upgrades it to the actual
// cheapest-allowed pair as soon as /v1/usage resolves.
const FALLBACK_PROVIDER: Provider = 'gemini';
const FALLBACK_MODEL = 'gemini-2.5-flash-lite';

function emptyForm(allowed: readonly string[]): FormState {
  const provider = cheapestProviderFor(allowed) ?? FALLBACK_PROVIDER;
  const model = cheapestAllowedModel(allowed) ?? FALLBACK_MODEL;
  return {
    name: '',
    systemPrompt: '',
    provider,
    model,
    temperature: '',
    maxOutputTokens: '',
    maxCostUsd: '0.10',
    toolsJson: '[]',
    allowedDomainsText: '',
    allowedOriginsText: '',
    fallbackEnabled: false,
    fallbackProvider: provider,
    fallbackModel: '',
  };
}

function fromAgent(agent: Agent): FormState {
  return {
    name: agent.name,
    systemPrompt: agent.systemPrompt,
    provider: agent.modelPolicy.provider,
    model: agent.modelPolicy.model,
    temperature:
      agent.modelPolicy.temperature !== undefined ? String(agent.modelPolicy.temperature) : '',
    maxOutputTokens:
      agent.modelPolicy.maxOutputTokens !== undefined
        ? String(agent.modelPolicy.maxOutputTokens)
        : '',
    maxCostUsd: String(agent.budget.maxCostUsd),
    toolsJson: JSON.stringify(agent.tools, null, 2),
    allowedDomainsText: agent.allowedDomains.join('\n'),
    allowedOriginsText: agent.allowedOrigins.join('\n'),
    fallbackEnabled: agent.fallback !== null,
    fallbackProvider: (agent.fallback?.provider ?? 'openai'),
    fallbackModel: agent.fallback?.model ?? '',
  };
}

function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

interface BuildResult {
  ok: true;
  body: CreateAgentInput;
}
interface BuildErrors {
  ok: false;
  errors: Partial<Record<keyof FormState, string>>;
}
type Built = BuildResult | BuildErrors;

function buildBody(form: FormState): Built {
  const errors: Partial<Record<keyof FormState, string>> = {};

  if (!form.name.trim()) errors.name = 'required';
  if (!form.systemPrompt.trim()) errors.systemPrompt = 'required';
  if (!form.model.trim()) errors.model = 'required';

  let temperature: number | undefined;
  if (form.temperature.trim() !== '') {
    const t = Number(form.temperature);
    if (!Number.isFinite(t) || t < 0 || t > 2) errors.temperature = 'must be 0–2';
    else temperature = t;
  }

  let maxOutputTokens: number | undefined;
  if (form.maxOutputTokens.trim() !== '') {
    const v = Number(form.maxOutputTokens);
    if (!Number.isInteger(v) || v < 1 || v > 16_384) errors.maxOutputTokens = 'must be 1–16384';
    else maxOutputTokens = v;
  }

  const maxCostUsd = Number(form.maxCostUsd);
  if (!Number.isFinite(maxCostUsd) || maxCostUsd < 0 || maxCostUsd > 100) {
    errors.maxCostUsd = 'must be 0–100';
  }

  let tools: ToolDefinition[] = [];
  try {
    const parsed: unknown = JSON.parse(form.toolsJson);
    if (!Array.isArray(parsed)) {
      errors.toolsJson = 'must be a JSON array';
    } else {
      tools = parsed as ToolDefinition[];
      if (tools.length > 16) errors.toolsJson = 'max 16 tools';
    }
  } catch (e) {
    errors.toolsJson = e instanceof Error ? e.message : 'invalid JSON';
  }

  let fallback: AgentFallback | undefined;
  if (form.fallbackEnabled) {
    if (!form.fallbackModel.trim()) {
      errors.fallbackModel = 'required when fallback is enabled';
    } else {
      fallback = { provider: form.fallbackProvider, model: form.fallbackModel.trim() };
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const modelPolicy: AgentModelPolicy = {
    provider: form.provider,
    model: form.model.trim(),
    ...(temperature !== undefined ? { temperature } : {}),
    ...(maxOutputTokens !== undefined ? { maxOutputTokens } : {}),
  };

  const body: CreateAgentInput = {
    name: form.name.trim(),
    systemPrompt: form.systemPrompt,
    modelPolicy,
    budget: { maxCostUsd, maxSteps: 8, wallClockMs: 30_000 },
    tools,
    allowedDomains: splitLines(form.allowedDomainsText),
    allowedOrigins: splitLines(form.allowedOriginsText),
    ...(fallback ? { fallback } : {}),
  };

  return { ok: true, body };
}

interface AgentFormProps {
  initial?: Agent;
  mode: 'create' | 'edit';
}

// Deterministic id derivation so <ErrorSummary> can locate a field via
// document.getElementById without threading refs back from each <Field>.
// The dashboard renders one AgentForm per route, so id collisions across
// concurrent forms aren't a concern.
function fieldId(name: keyof FormState): string {
  return `agent-field-${name}`;
}

// Human-readable labels for the error summary. Only the keys that
// `buildBody()` can produce errors for need entries here; missing entries
// are filtered out at the call site.
const ERROR_LABELS: Partial<Record<keyof FormState, string>> = {
  name: 'Name',
  systemPrompt: 'System prompt',
  model: 'Model',
  temperature: 'Temperature',
  maxOutputTokens: 'Max output tokens',
  maxCostUsd: 'Max cost per run (USD)',
  toolsJson: 'Tool definitions',
  fallbackModel: 'Fallback model',
};

export function AgentForm({ initial, mode }: AgentFormProps) {
  const api = useApiClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Plan-aware model menu (revisao-v2.md §6 #1 follow-up). The summary
  // query is cached + deduped by TanStack Query — pages like Home and
  // Billing already issue it, so this rarely costs a fetch.
  const usageQuery = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });
  const allowedModels = usageQuery.data?.limits?.allowedModels ?? [];
  const planCode = usageQuery.data?.plan.code ?? null;

  const [form, setForm] = useState<FormState>(() =>
    initial ? fromAgent(initial) : emptyForm(allowedModels),
  );
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  // Increments on every failed submit so <ErrorSummary> re-focuses the
  // heading even when the same set of errors is produced twice in a row.
  const [submitFailCount, setSubmitFailCount] = useState(0);

  // Re-seed when navigating between agents (id changes).
  useEffect(() => {
    if (initial) setForm(fromAgent(initial));
  }, [initial]);

  // Upgrade the create-form's pre-load defaults once /v1/usage resolves.
  // Only runs in `create` mode and only while the user hasn't typed
  // anything — once they've started filling fields, leave their input alone.
  useEffect(() => {
    if (mode !== 'create' || initial) return;
    if (allowedModels.length === 0) return;
    if (form.name.length > 0 || form.systemPrompt.length > 0) return;
    setForm((f) => {
      const provider = cheapestProviderFor(allowedModels) ?? f.provider;
      const model = cheapestAllowedModel(allowedModels) ?? f.model;
      if (provider === f.provider && model === f.model) return f;
      return { ...f, provider, model, fallbackProvider: provider };
    });
    // Intentionally narrow deps so the upgrade fires once when allowedModels
    // arrives, not on every keystroke. The `react-hooks/exhaustive-deps`
    // rule isn't configured in this project's eslint, so no disable
    // directive is needed — leaving this comment as the rationale.
  }, [allowedModels.length, mode, initial]);

  // True when the provider has at least one model in the tenant's
  // allowed_models set. Drives the `disabled` flag on the provider <option>
  // — locking out providers the plan can't actually use prevents the
  // broken-state UX where switching to (e.g.) Anthropic on Free leaves
  // the form with zero pickable models.
  const providerHasAllowedModels = (p: Provider): boolean =>
    (MODELS_BY_PROVIDER[p] as readonly string[]).some((m) => allowedModels.includes(m));

  // Sort by price rank ascending. Pulled out as a helper because the
  // primary + fallback model dropdowns share the exact ordering rule.
  // Cast through `Record<string, number | undefined>` so an unknown model
  // (e.g. legacy edit-case where the agent points at a model the plan no
  // longer ranks) returns `undefined` and falls back to `Infinity`,
  // sorting it last instead of throwing.
  const priceOf = (m: string): number =>
    (MODEL_PRICE_RANK as Record<string, number | undefined>)[m] ?? Infinity;
  const sortByPrice = (a: string, b: string): number => priceOf(a) - priceOf(b);

  // Model options for the currently selected provider, intersected with
  // the plan's allowedModels and sorted cheapest-first via MODEL_PRICE_RANK.
  // Edit-case (mode === 'edit' only): if the agent already points at a
  // model the plan no longer allows (cap shrunk, plan downgraded, etc.),
  // keep that model in the list so the form can render and the user can
  // pick a replacement — the server's allowed_models gate is the
  // authority on save. In create mode we never include a non-allowed
  // model — combined with the disabled provider gate above, the user
  // can't reach a state with zero pickable models.
  const modelOptions = useMemo(() => {
    const inProvider = MODELS_BY_PROVIDER[form.provider] as readonly string[];
    const allowedInProvider = inProvider.filter((m) => allowedModels.includes(m));
    if (
      mode === 'edit' &&
      form.model &&
      inProvider.includes(form.model) &&
      !allowedInProvider.includes(form.model)
    ) {
      allowedInProvider.push(form.model);
    }
    return [...allowedInProvider].sort(sortByPrice);
  }, [form.provider, form.model, allowedModels, mode]);

  const fallbackModelOptions = useMemo(() => {
    const inProvider = MODELS_BY_PROVIDER[form.fallbackProvider] as readonly string[];
    const allowedInProvider = inProvider.filter((m) => allowedModels.includes(m));
    if (
      mode === 'edit' &&
      form.fallbackModel &&
      inProvider.includes(form.fallbackModel) &&
      !allowedInProvider.includes(form.fallbackModel)
    ) {
      allowedInProvider.push(form.fallbackModel);
    }
    return [...allowedInProvider].sort(sortByPrice);
  }, [form.fallbackProvider, form.fallbackModel, allowedModels, mode]);

  // Provider change resets model to the cheapest of the new provider's
  // allowed set. Won't fire for locked providers (their option is disabled),
  // but if it somehow does (programmatic state set), we keep the previous
  // model rather than silently defaulting to a not-in-plan one.
  function changeProvider(next: Provider): void {
    setForm((f) => {
      const inNext = MODELS_BY_PROVIDER[next] as readonly string[];
      const cheapestInNext = inNext.find((m) => allowedModels.includes(m));
      // No allowed model in the new provider — keep the prior model so the
      // form doesn't auto-pick a not-in-plan model. UI prevents this path
      // by disabling locked provider options anyway; this is belt-and-braces.
      if (!cheapestInNext) return { ...f, provider: next };
      return { ...f, provider: next, model: cheapestInNext };
    });
    setFieldErrors((prev) => {
      if (!('model' in prev)) return prev;
      const next = { ...prev };
      delete next.model;
      return next;
    });
  }

  function changeFallbackProvider(next: Provider): void {
    setForm((f) => {
      const inNext = MODELS_BY_PROVIDER[next] as readonly string[];
      const cheapestInNext = inNext.find((m) => allowedModels.includes(m)) ?? '';
      return { ...f, fallbackProvider: next, fallbackModel: cheapestInNext };
    });
  }

  const upsert = useMutation({
    mutationFn: async (body: CreateAgentInput): Promise<Agent> => {
      if (mode === 'edit' && initial) {
        return agentsApi.update(api, initial.id, body);
      }
      return agentsApi.create(api, body);
    },
    onSuccess: (agent) => {
      void queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: agentQueryKey(agent.id) });
      toast.success(mode === 'edit' ? 'Agent saved' : `Created ${agent.name}`);
      navigate('/agents');
    },
    onError: (err) => {
      // Top-level mutation failures (network / 5xx / validation 422) surface
      // as a toast so the error is visible regardless of scroll position.
      // Per-field validation continues to render inline under the input.
      toast.error(mode === 'edit' ? 'Could not save agent' : 'Could not create agent', {
        description: friendlyError(err, 'Failed to reach the API.'),
      });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const built = buildBody(form);
    if (!built.ok) {
      setFieldErrors(built.errors);
      // Bump the focus key BEFORE the heading mounts (next render) so the
      // useEffect inside <ErrorSummary> sees a new value.
      setSubmitFailCount((n) => n + 1);
      return;
    }
    setFieldErrors({});
    upsert.mutate(built.body);
  }

  // Auto-clear an error as soon as the user edits the offending field — the
  // summary shrinks live so the user sees they're making progress instead of
  // having to submit again to find out.
  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      // Destructure-omit instead of `delete next[key]` — the dynamic
      // delete trips no-dynamic-delete and the rest pattern is the
      // idiomatic immutable equivalent.
      const { [key]: _omitted, ...rest } = prev;
      return rest;
    });
  }

  const errorList: ErrorSummaryItem[] = (
    Object.entries(fieldErrors) as [keyof FormState, string | undefined][]
  ).flatMap(([key, message]) => {
    const label = ERROR_LABELS[key];
    if (!label || !message) return [];
    return [{ fieldId: fieldId(key), label, message }];
  });

  return (
    // Negative bottom margin cancels the AppShell's main wrapper padding
    // (`p-4 sm:p-6 lg:p-8`) so the form's bottom edge coincides with the
    // scroll container's bottom edge. Without this, the wrapper's
    // `pb-{4,6,8}` leaves 16-32px of empty space below the sticky save bar
    // when the user scrolls to the very end of the form. The horizontal
    // bleed (`-mx-*`) on the save bar already does the equivalent for the
    // sides; this just extends that idea down.
    <form className="-mb-4 space-y-6 sm:-mb-6 lg:-mb-8" onSubmit={onSubmit} noValidate>
      <ErrorSummary errors={errorList} focusKey={submitFailCount} />

      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field name="name" label="Name" required error={fieldErrors.name}>
            <Input
              value={form.name}
              onChange={(e) => { update('name', e.target.value); }}
              maxLength={128}
            />
          </Field>
          <Field
            name="systemPrompt"
            label="System prompt"
            required
            help="Tell your AI how to behave. Example: &lsquo;You are a friendly support agent for Acme Corp. Always be concise.&rsquo;"
            error={fieldErrors.systemPrompt}
          >
            <Textarea
              value={form.systemPrompt}
              onChange={(e) => { update('systemPrompt', e.target.value); }}
              maxLength={10_000}
              rows={6}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Model</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field name="provider" label="Provider">
            <Select
              value={form.provider}
              onChange={(e) => { changeProvider(e.target.value as Provider); }}
            >
              {PROVIDERS.map((p) => {
                // Lock providers with zero allowed models on this plan, EXCEPT
                // the agent's currently-selected provider (edit mode could
                // have us on a now-unavailable provider — render it so the
                // user can switch to a different one).
                const hasModels = providerHasAllowedModels(p);
                const isCurrent = p === form.provider;
                return (
                  <option key={p} value={p} disabled={!hasModels && !isCurrent}>
                    {p}
                    {!hasModels ? ' (upgrade to unlock)' : ''}
                  </option>
                );
              })}
            </Select>
          </Field>
          <Field
            name="model"
            label="Model"
            required
            help={
              planCode
                ? `Cheaper models are faster and lower-cost. Showing the ${planCode} plan's available models, cheapest first.`
                : 'Cheaper models are faster and lower-cost. Loading your plan’s available models…'
            }
            error={fieldErrors.model}
          >
            <Select
              value={form.model}
              onChange={(e) => { update('model', e.target.value); }}
              disabled={modelOptions.length === 0}
              required
            >
              {modelOptions.length === 0 ? (
                <option value="" disabled>
                  No {form.provider} models available on this plan — switch provider
                </option>
              ) : (
                modelOptions.map((m) => {
                  const allowed = allowedModels.includes(m);
                  return (
                    <option key={m} value={m}>
                      {m}
                      {!allowed ? ' (not in current plan)' : ''}
                    </option>
                  );
                })
              )}
            </Select>
          </Field>
          <Field
            name="temperature"
            label="Temperature"
            help="Higher = more creative. Lower = more predictable. 0.7 works well for most assistants."
            error={fieldErrors.temperature}
          >
            <Input
              value={form.temperature}
              onChange={(e) => { update('temperature', e.target.value); }}
              inputMode="decimal"
              placeholder="0.7"
            />
          </Field>
          <Field name="maxOutputTokens" label="Max output tokens" error={fieldErrors.maxOutputTokens}>
            <Input
              value={form.maxOutputTokens}
              onChange={(e) => { update('maxOutputTokens', e.target.value); }}
              inputMode="numeric"
              placeholder="1024"
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Fallback</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.fallbackEnabled}
              onChange={(e) => { update('fallbackEnabled', e.target.checked); }}
            />
            Enable fallback (provider/model used on 5xx or timeout)
          </label>
          {form.fallbackEnabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field name="fallbackProvider" label="Fallback provider">
                <Select
                  value={form.fallbackProvider}
                  onChange={(e) => { changeFallbackProvider(e.target.value as Provider); }}
                >
                  {PROVIDERS.map((p) => {
                    const hasModels = providerHasAllowedModels(p);
                    const isCurrent = p === form.fallbackProvider;
                    return (
                      <option key={p} value={p} disabled={!hasModels && !isCurrent}>
                        {p}
                        {!hasModels ? ' (upgrade to unlock)' : ''}
                      </option>
                    );
                  })}
                </Select>
              </Field>
              <Field name="fallbackModel" label="Fallback model" required error={fieldErrors.fallbackModel}>
                <Select
                  value={form.fallbackModel}
                  onChange={(e) => { update('fallbackModel', e.target.value); }}
                  disabled={fallbackModelOptions.length === 0}
                  required
                >
                  {fallbackModelOptions.length === 0 ? (
                    <option value="" disabled>
                      No {form.fallbackProvider} models available on this plan
                    </option>
                  ) : (
                    <>
                      <option value="">— select a model —</option>
                      {fallbackModelOptions.map((m) => {
                        const allowed = allowedModels.includes(m);
                        return (
                          <option key={m} value={m}>
                            {m}
                            {!allowed ? ' (not in current plan)' : ''}
                          </option>
                        );
                      })}
                    </>
                  )}
                </Select>
              </Field>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Budget</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field
            name="maxCostUsd"
            label="Max cost per run (USD)"
            required
            error={fieldErrors.maxCostUsd}
            help="Hard limit per chat. Cortex stops the AI if it spends more than this on a single conversation, so a runaway tool loop can&rsquo;t drain your budget."
          >
            <Input
              value={form.maxCostUsd}
              onChange={(e) => { update('maxCostUsd', e.target.value); }}
              inputMode="decimal"
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tools (JSON)</CardTitle>
        </CardHeader>
        <CardContent>
          <Field
            name="toolsJson"
            label="Tool definitions"
            error={fieldErrors.toolsJson}
            help="Optional capabilities the assistant can call. JSON array of tool definitions (name, description, parameters). Max 16. Examples: search_kb (cite from your uploaded documents), fetch_url (look up live data on approved sites)."
          >
            <Textarea
              value={form.toolsJson}
              onChange={(e) => { update('toolsJson', e.target.value); }}
              rows={12}
              spellCheck={false}
              className="font-mono text-xs"
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allowlists</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field
            name="allowedDomainsText"
            label="Allowed tool domains"
            help="One hostname per line. Max 32."
          >
            <Textarea
              value={form.allowedDomainsText}
              onChange={(e) => { update('allowedDomainsText', e.target.value); }}
              rows={6}
              spellCheck={false}
              className="font-mono text-xs"
            />
          </Field>
          <Field
            name="allowedOriginsText"
            label="Allowed widget origins"
            help="The websites where this assistant is allowed to appear. Add your site here, like https://www.example.com — one per line. The widget refuses to load anywhere else."
          >
            <Textarea
              value={form.allowedOriginsText}
              onChange={(e) => { update('allowedOriginsText', e.target.value); }}
              rows={6}
              spellCheck={false}
              className="font-mono text-xs"
            />
          </Field>
        </CardContent>
      </Card>

      {/* Sticky save bar — stays pinned to the bottom of the viewport
          while form content scrolls. Sized as the last child of the form
          (no extra bottom padding) so when the user scrolls all the way
          down, the bar lands flush against the page bottom with no empty
          space below it. The 24px gap above is supplied by the form's
          `space-y-6`, matching the rhythm between cards. */}
      <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t bg-card/95 px-4 py-3 backdrop-blur-sm sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <Button type="button" variant="ghost" onClick={() => { navigate('/agents'); }}>
          Cancel
        </Button>
        <Button type="submit" disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

interface FieldProps {
  // Form-state key. Drives the deterministic DOM id used by both the input
  // and the <ErrorSummary> click-to-focus links.
  name: keyof FormState;
  label: string;
  required?: boolean;
  error?: string | undefined;
  help?: string;
  children: ReactElement<{
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
    'aria-required'?: boolean;
  }>;
}

// Wires <Label htmlFor>, the help/error <p>, and aria-invalid + aria-required
// onto the wrapped input. The asterisk on required-field labels is rendered
// via a CSS `::after` pseudo-element — that keeps the asterisk visible but
// out of the label's text content, so the accessible name stays "Name" (not
// "Name *") and queries like getByLabelText('Name') / getByRole('textbox',
// { name: 'Name' }) keep working. aria-required carries the semantics for
// screen readers; the visual asterisk is the sighted-user cue.
function Field({ name, label, required, error, help, children }: FieldProps) {
  const id = fieldId(name);
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  // aria-describedby points at whichever <p> below is rendered (error wins
  // when both are present — the help text is hidden in that branch).
  const describedBy = error ? errorId : help ? helpId : undefined;
  const child = cloneElement(children, {
    id,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(required ? { 'aria-required': true } : {}),
    ...(error ? { 'aria-invalid': true } : {}),
  });
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={id}
        className={required ? 'after:ml-0.5 after:text-destructive after:content-["*"]' : undefined}
      >
        {label}
      </Label>
      {child}
      {help && !error && (
        <p id={helpId} className="text-xs text-muted-foreground">
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
